import { useState } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ManageUseCaseTable } from './ManageUseCaseTable.tsx';
import { SettingsIconButton } from '../../common/SettingsIconButton.tsx';
import React from 'react';

/*
 * Legacy: full `PageSettingsDialog` + MANAGE_USE_CASE_SETTINGS (display/sort/filter toggles).
 * Replaced by minimal modal to match Zone Management settings UX.
 *
 * import { PageSettingsDialog } from '../../common/PageSettingsDialog.tsx';
 * import { SettingsGroup } from '../../common/SettingsGroup.tsx';
 * import { SettingsSwitch, SettingsSelect } from '../../common/SettingsControls.tsx';
 */

interface ManageUseCaseTabProps {
  /** When true, this tab is the active one (used to refetch data when user switches to tab). */
  isActive?: boolean;
  /** Administration: layout fits nested page (API-backed use cases). */
  administrationMode?: boolean;
  /**
   * When true with administrationMode: no outer Paper/motion — parent shell provides chrome
   * (e.g. Administration AI Models inner tabs, same pattern as User Management).
   */
  embedded?: boolean;
  /** Overrides default table title for the current context. */
  tableTitle?: string;
}

export function ManageUseCaseTab({
  isActive = true,
  administrationMode = false,
  embedded = false,
  tableTitle,
}: ManageUseCaseTabProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (embedded && administrationMode) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <ManageUseCaseTable
          title={tableTitle ?? ''}
          isActive={isActive}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
    >
      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ minHeight: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <Paper
          elevation={1}
          sx={{
            ...(administrationMode
              ? {
                  minHeight: 480,
                  height: 'auto',
                  maxHeight: 'none',
                  flex: 1,
                }
              : {
                  // Header + app footer + tabs + padding (aligns card bottom with layout footer)
                  height: 'calc(100vh - 168.8px)',
                  maxHeight: 'calc(100vh - 168.8px)',
                }),
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: "20px",
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {!administrationMode ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
              }}
            >
              <SettingsIconButton
                onClick={() => setSettingsOpen(true)}
                isOpen={settingsOpen}
                tooltipText="Manage Use Case Settings"
                ariaLabel="Configure Manage Use Case settings"
              />
            </Box>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <ManageUseCaseTable
              title={
                tableTitle ??
                (administrationMode
                  ? "AI Models Management"
                  : "Manage Use Case")
              }
              isActive={isActive}
            />
          </Box>
        </Paper>
      </motion.div>

      {!administrationMode ? (
        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth="xs"
          fullWidth
          aria-labelledby="manage-use-case-settings-title"
        >
          <DialogTitle
            id="manage-use-case-settings-title"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 18,
              fontWeight: 600,
              color: '#000000',
            }}
          >
            Settings
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: 14,
                color: '#212121',
              }}
            >
              Upcoming feature
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setSettingsOpen(false)}
              variant="contained"
              color="primary"
              sx={{
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                borderRadius: 1,
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/*
      Previous full settings UI (PageSettingsDialog + SettingsGroup / SettingsSwitch / SettingsSelect).
      const MANAGE_USE_CASE_SETTINGS = { DISPLAY: {...}, SORTING: {...}, FILTERING: {...}, FEATURES: {...} };
      const [showDescriptions, setShowDescriptions] = useState(...);
      ... handleResetSettings ...
      <PageSettingsDialog open={settingsOpen} onClose=... title="Manage Use Case Settings" onReset={handleResetSettings}>
        ...
      </PageSettingsDialog>
      */}
    </Box>
  );
}
